import { Button, Card, Drawer, Input, Popconfirm, Select, Switch, Tag } from "@arco-design/web-react";
import { Building2, MapPin, Pencil, Plus, RotateCcw, Search, Trash2, X } from "lucide-react";
import type { ReactElement, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";

import { useCurrentAuthUser } from "../../auth";
import { canUsePermission } from "../../../lib/auth";
import {
  GeoOperationPanel,
  type GeoBusinessCandidate,
  type GeoBusinessVerifyResult,
  type GeoOperationStatus,
} from "../../../shared/business-geo";
import {
  getCityOptions,
  getDistrictOptions,
  getProvinceOptions,
} from "../../../shared/region";
import {
  crmOrganizationCollegeType,
  crmOrganizationPageSizeOptions,
  crmOrganizationSiteTypeOptions,
  crmOrganizationSubtypeOptions,
  crmOrganizationTypeOptions,
} from "../config";
import {
  emptyCrmOrganizationMutation,
  emptyCrmOrganizationSiteMutation,
  useCrmOrganizationRuntime,
} from "../runtime/useCrmOrganizationRuntime";
import type {
  CrmCampusDiscoveryCandidate,
  CrmCampusDiscoveryPendingItem,
  CrmCampusDiscoverySummary,
  CrmOrganizationItem,
  CrmOrganizationMutation,
  CrmOrganizationSiteItem,
  CrmOrganizationSiteMutation,
} from "../types";
import { shouldUseManualDistrictInput, applyGeoCandidateToOrganizationMutation, applyGeoCandidateToSiteMutation } from "../service/crmOrganizationService";
import "./CrmOrganizationPage.css";
import "../../../shared/ui/crmListPageLayout.css";

type OrganizationModalState =
  | { type: "create" }
  | { type: "edit"; organization: CrmOrganizationItem };

type SiteModalState = { type: "create" } | { type: "edit"; site: CrmOrganizationSiteItem };

/** 学校组织页面主体。 */
export function CrmOrganizationFeaturePage() {
  const searchParams = new URLSearchParams(window.location.search);
  const deepLinkSiteId = searchParams.get("geoEditSiteId") ?? undefined;
  const deepLinkCreateSite = searchParams.get("geoCreateSite") === "1";
  const deepLinkOrgName = searchParams.get("geoOrgName") ?? undefined;
  const runtime = useCrmOrganizationRuntime(deepLinkOrgName);
  const currentUser = useCurrentAuthUser().data;
  const canManage = canUsePermission(currentUser, "system.organization:manage");
  const canDelete = canUsePermission(currentUser, "system.organization:delete");
  const [organizationModalState, setOrganizationModalState] = useState<OrganizationModalState | null>(null);
  const [siteModalState, setSiteModalState] = useState<SiteModalState | null>(null);
  const selectedDeepLinkOrgRef = useRef<string | undefined>(undefined);
  const openedDeepLinkSiteRef = useRef<string | undefined>(undefined);
  const openedDeepLinkCreateSiteRef = useRef<string | undefined>(undefined);
  const totalPages = Math.max(Math.ceil(runtime.total / runtime.pageSize), 1);

  useEffect(() => {
    if ((!deepLinkSiteId && !deepLinkCreateSite) || !deepLinkOrgName) return;
    const deepLinkKey = deepLinkSiteId ?? `create:${deepLinkOrgName}`;
    if (selectedDeepLinkOrgRef.current === deepLinkKey) return;
    const organization = runtime.items.find((item) => item.orgName === deepLinkOrgName);
    if (!organization) return;
    selectedDeepLinkOrgRef.current = deepLinkKey;
    runtime.setSelectedOrganizationId(organization.id);
  }, [
    deepLinkCreateSite,
    deepLinkOrgName,
    deepLinkSiteId,
    runtime.items,
    runtime.setSelectedOrganizationId,
  ]);

  useEffect(() => {
    if (!deepLinkSiteId || openedDeepLinkSiteRef.current === deepLinkSiteId) return;
    const site = runtime.selectedOrganization?.sites.find(
      (item) => String(item.id) === deepLinkSiteId,
    );
    if (!site) return;
    openedDeepLinkSiteRef.current = deepLinkSiteId;
    runtime.hydrateSavedSiteGeo(site);
    setSiteModalState({ type: "edit", site });
  }, [deepLinkSiteId, runtime.selectedOrganization]);

  useEffect(() => {
    if (!deepLinkCreateSite || !deepLinkOrgName) return;
    if (openedDeepLinkCreateSiteRef.current === deepLinkOrgName) return;
    if (runtime.selectedOrganization?.orgName !== deepLinkOrgName) return;
    openedDeepLinkCreateSiteRef.current = deepLinkOrgName;
    runtime.clearGeoFeedback();
    setSiteModalState({ type: "create" });
  }, [deepLinkCreateSite, deepLinkOrgName, runtime.selectedOrganization]);

  return (
    <main className="crm-organization-page">

      <section
        className="crm-list-filter-panel crm-organization-filter"
        role="search"
        aria-label="学校组织筛选"
        data-layout="two-tier-responsive"
      >
        <div className="crm-list-filter-fields crm-organization-filter__fields">
          <div className="crm-list-filter-primary">
            <label className="crm-list-field crm-list-field--lg">
              <span>学校名称/拼音码</span>
              <Input
                allowClear
                placeholder="学校名称或拼音码"
                value={runtime.draftFilters.keyword ?? ""}
                onChange={(keyword) => runtime.setDraftFilters({ ...runtime.draftFilters, keyword })}
                onPressEnter={runtime.handleSearch}
              />
            </label>
            <div className="crm-list-field crm-list-field--md">
              <span>单位类型</span>
              <Select
                allowClear
                showSearch
                aria-label="单位类型"
                data-testid="crm-organization-org-type-filter"
                getPopupContainer={filterPopupContainer}
                placeholder="全部类型"
                options={crmOrganizationTypeOptions.map((value) => ({ label: value, value }))}
                value={runtime.draftFilters.orgType}
                onChange={(orgType) => runtime.setDraftFilters({
                  ...runtime.draftFilters,
                  orgType: orgType || undefined,
                })}
              />
            </div>
            <div className="crm-list-field crm-list-field--md">
              <span>省份</span>
              <Select
                allowClear
                showSearch
                aria-label="省份筛选"
                data-testid="crm-organization-province-filter"
                getPopupContainer={filterPopupContainer}
                placeholder="全部省份"
                options={getProvinceOptions().map((value) => ({ label: value, value }))}
                value={runtime.draftFilters.province || undefined}
                onChange={(province) => runtime.setDraftFilters({
                  ...runtime.draftFilters,
                  province: province || undefined,
                  city: undefined,
                  district: undefined,
                })}
              />
            </div>
            <div className="crm-list-field crm-list-field--md">
              <span>城市</span>
              <Select
                allowClear
                showSearch
                aria-label="城市筛选"
                data-testid="crm-organization-city-filter"
                disabled={!runtime.draftFilters.province}
                getPopupContainer={filterPopupContainer}
                placeholder="全部城市"
                options={(runtime.draftFilters.province
                  ? getCityOptions(runtime.draftFilters.province)
                  : []
                ).map((value) => ({ label: value, value }))}
                value={runtime.draftFilters.city || undefined}
                onChange={(city) => runtime.setDraftFilters({
                  ...runtime.draftFilters,
                  city: city || undefined,
                  district: undefined,
                })}
              />
            </div>
            <div className="crm-list-field crm-list-field--md">
              <span>区县</span>
              <Select
                allowClear
                showSearch
                aria-label="区县筛选"
                data-testid="crm-organization-district-filter"
                disabled={!runtime.draftFilters.city}
                getPopupContainer={filterPopupContainer}
                placeholder="全部区县"
                options={(runtime.draftFilters.province && runtime.draftFilters.city
                  ? getDistrictOptions(runtime.draftFilters.province, runtime.draftFilters.city)
                  : []
                ).map((value) => ({ label: value, value }))}
                value={runtime.draftFilters.district || undefined}
                onChange={(district) => runtime.setDraftFilters({
                  ...runtime.draftFilters,
                  district: district || undefined,
                })}
              />
            </div>
            <div className="crm-list-field crm-list-field--sm">
              <span>状态</span>
              <Select
                allowClear
                aria-label="状态"
                getPopupContainer={filterPopupContainer}
                placeholder="全部状态"
                options={[
                  { label: "启用", value: "false" },
                  { label: "停用", value: "true" },
                ]}
                value={
                  runtime.draftFilters.isDisabled === undefined
                    ? undefined
                    : String(runtime.draftFilters.isDisabled)
                }
                onChange={(value) => runtime.setDraftFilters({
                  ...runtime.draftFilters,
                  isDisabled: value === undefined || value === "" ? undefined : value === "true",
                })}
              />
            </div>
          </div>
        </div>
        <div className="crm-list-filter-actions crm-organization-filter__actions">
          <Button aria-label="重置筛选" icon={<RotateCcw size={15} />} onClick={runtime.handleReset}>
            重置
          </Button>
          <Button type="primary" icon={<Search size={15} />} onClick={runtime.handleSearch}>
            查询
          </Button>
        </div>
      </section>
      {!(organizationModalState || runtime.selectedOrganizationId) &&
      (runtime.errorMessage || runtime.mutationErrorMessage) ? (
        <div className="crm-organization-error" role="alert">
          {runtime.errorMessage || runtime.mutationErrorMessage}
        </div>
      ) : null}

      <Card className="crm-organization-card" bodyStyle={{ padding: 0 }}>
        <div className="crm-organization-table-toolbar">
          <strong>学校组织列表</strong>
          <Button
            type="primary"
            icon={<Plus size={16} />}
            disabled={!canManage}
            onClick={() => {
              runtime.clearMutationError();
              runtime.clearGeoFeedback();
              setOrganizationModalState({ type: "create" });
            }}
          >
            新增学校组织
          </Button>
        </div>
        <div className="crm-organization-table-scroll">
          <table className="crm-organization-table">
            <thead>
              <tr>
                <th>学校名称</th>
                <th>拼音码</th>
                <th>省市区</th>
                <th>主校区</th>
                <th>校区数</th>
                <th>状态</th>
                <th>更新时间</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {runtime.loading ? (
                <tr>
                  <td colSpan={8} className="crm-organization-empty">
                    正在加载学校组织...
                  </td>
                </tr>
              ) : null}
              {!runtime.loading && runtime.items.length === 0 ? (
                <tr>
                  <td colSpan={8} className="crm-organization-empty">
                    暂无学校组织数据
                  </td>
                </tr>
              ) : null}
              {!runtime.loading
                ? runtime.items.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <strong>{item.orgName}</strong>
                        <small>
                          {[item.orgType, item.orgSubtype].filter(Boolean).join(" / ")}
                        </small>
                      </td>
                      <td>{item.spellCode}</td>
                      <td>{[item.province, item.city, item.district].filter(Boolean).join(" / ")}</td>
                      <td>{item.mainSiteName ?? "未设置"}</td>
                      <td>{item.siteCount}</td>
                      <td>
                        <Tag color={item.isDisabled ? "orangered" : "green"}>{item.isDisabledLabel}</Tag>
                      </td>
                      <td>{item.updatedAt}</td>
                      <td>
                        <div className="crm-organization-row-actions">
                          <Button
                            aria-label={`编辑学校 ${item.orgName}`}
                            type="text"
                            icon={<Pencil size={15} />}
                            disabled={!canManage}
                            onClick={() => {
                              runtime.clearMutationError();
                              runtime.clearGeoFeedback();
                              setOrganizationModalState({ type: "edit", organization: item });
                            }}
                          />
                          <Button
                            aria-label={`维护校区 ${item.orgName}`}
                            type="text"
                            icon={<MapPin size={15} />}
                            onClick={() => {
                              runtime.clearMutationError();
                              runtime.setSelectedOrganizationId(item.id);
                            }}
                          />
                          <Popconfirm
                            position="left"
                            getPopupContainer={filterPopupContainer}
                            title={`确认删除“${item.orgName}”？删除后不可恢复。`}
                            okText="删除"
                            cancelText="取消"
                            okButtonProps={{ status: "danger" }}
                            disabled={!canDelete}
                            onOk={() => runtime.handleDeleteOrganization(item)}
                          >
                            <span className="crm-organization-delete-trigger">
                              <Button
                                aria-label={`删除学校 ${item.orgName}`}
                                type="text"
                                status="danger"
                                disabled={!canDelete}
                                icon={<Trash2 size={15} />}
                                onClick={() => undefined}
                              />
                            </span>
                          </Popconfirm>
                        </div>
                      </td>
                    </tr>
                  ))
                : null}
            </tbody>
          </table>
        </div>
        <div className="crm-organization-pagination">
          <span>共 {runtime.total} 条</span>
          <Button disabled={runtime.page <= 1} onClick={() => runtime.handlePageChange(runtime.page - 1)}>
            上一页
          </Button>
          <span>
            {runtime.page} / {totalPages}
          </span>
          <Button disabled={runtime.page >= totalPages} onClick={() => runtime.handlePageChange(runtime.page + 1)}>
            下一页
          </Button>
          <select
            aria-label="每页条数"
            value={runtime.pageSize}
            onChange={(event) => runtime.handlePageSizeChange(Number(event.target.value))}
          >
            {crmOrganizationPageSizeOptions.map((option) => (
              <option key={option} value={option}>
                {option} 条
              </option>
            ))}
          </select>
        </div>
      </Card>

      <OrganizationMutationModal
        state={organizationModalState}
        errorMessage={runtime.mutationErrorMessage}
        geoResolving={runtime.geoResolving}
        geoStatus={runtime.geoStatus}
        geoResult={runtime.geoResult}
        onClose={() => {
          runtime.clearMutationError();
          runtime.clearGeoFeedback();
          setOrganizationModalState(null);
        }}
        onCreate={runtime.handleCreateOrganization}
        onUpdate={runtime.handleUpdateOrganization}
        onResolveGeo={runtime.handleResolveOrganizationGeo}
      />
      <SiteManagementModal
        canManage={canManage}
        canDelete={canDelete}
        organization={runtime.selectedOrganization}
        loading={runtime.selectedOrganizationLoading}
        errorMessage={runtime.mutationErrorMessage}
        geoResolving={runtime.geoResolving}
        geoStatus={runtime.geoStatus}
        geoResult={runtime.geoResult}
        campusDiscoverySummary={
          runtime.campusDiscoveryOrganizationId === runtime.selectedOrganization?.id
            ? runtime.campusDiscoverySummary
            : undefined
        }
        campusDiscoveryRetrying={runtime.campusDiscoveryRetrying}
        siteModalState={siteModalState}
        onClose={() => {
          runtime.clearGeoFeedback();
          runtime.setSelectedOrganizationId(null);
        }}
        onOpenCreateSite={() => {
          runtime.clearGeoFeedback();
          setSiteModalState({ type: "create" });
        }}
        onOpenEditSite={(site) => {
          runtime.hydrateSavedSiteGeo(site);
          setSiteModalState({ type: "edit", site });
        }}
        onCloseSiteModal={() => {
          runtime.clearGeoFeedback();
          setSiteModalState(null);
        }}
        onCreateSite={runtime.handleCreateSite}
        onUpdateSite={runtime.handleUpdateSite}
        onDeleteSite={runtime.handleDeleteSite}
        onResolveGeo={runtime.handleResolveGeo}
        onPersistAdoptedSiteGeo={runtime.handlePersistAdoptedSiteGeo}
        onAdoptDiscoveryPending={runtime.handleAdoptDiscoveryPending}
        onDiscoverCampuses={
          runtime.selectedOrganization
            ? () => runtime.handleRetryCampusDiscovery(runtime.selectedOrganization!.id)
            : undefined
        }
      />
    </main>
  );
}

/** 展示高校创建后的校区发现数量、结果和重试入口。 */
/** 已自动创建或库内已有的校区，不再展示待确认候选。 */
function filterCampusDiscoverySummary(
  summary: CrmCampusDiscoverySummary,
  sites: CrmOrganizationSiteItem[],
): CrmCampusDiscoverySummary {
  const existingNames = new Set(
    sites
      .filter((site) => !site.isDisabled)
      .map((site) => site.siteName.replace(/\s+/g, "").toLowerCase()),
  );
  const pendingItems = summary.pendingItems.filter(
    (item) => !existingNames.has(item.siteName.replace(/\s+/g, "").toLowerCase()),
  );
  if (pendingItems.length === summary.pendingItems.length) {
    return summary;
  }
  const pendingCount = pendingItems.length;
  return {
    ...summary,
    pendingCount,
    pendingItems,
    failureReasons: pendingItems.map((item) => item.reason),
    status: pendingCount > 0
      ? "needs_confirmation"
      : summary.createdCount > 0 || summary.existingCount > 0
        ? "confirmed"
        : summary.status,
    retryable: pendingCount > 0 ? summary.retryable : false,
  };
}

/** 格式化校区发现结果中的地点文本。 */
function formatCampusDiscoveryLocation(
  province: string,
  city: string,
  district: string,
  address: string,
): string {
  const detail = address.trim();
  if (!detail) {
    return [province, city, district].filter(Boolean).join(" ");
  }
  const compactDetail = detail.replace(/\s+/g, "");
  const prefix = [province, city, district].filter(Boolean);
  if (prefix.some((part) => compactDetail.includes(part.replace(/\s+/g, "")))) {
    return detail;
  }
  return [...prefix, detail].join(" ");
}

/** 将校区发现候选转换为校区新增参数。 */
function buildSiteMutationFromDiscoveryCandidate(
  pending: CrmCampusDiscoveryPendingItem,
  candidate: CrmCampusDiscoveryCandidate,
): CrmOrganizationSiteMutation {
  return {
    siteName: pending.siteName,
    province: candidate.province || pending.province || undefined,
    city: candidate.city || pending.city || undefined,
    district: candidate.district || pending.district || undefined,
    address: candidate.address || pending.address,
    lng: candidate.longitude,
    lat: candidate.latitude,
    geoStatus: "unverified",
    isMainSite: false,
    isDisabled: false,
  };
}

/** 在无高德候选时按草稿地址创建待确认校区。 */
function buildSiteMutationFromDiscoveryDraft(
  pending: CrmCampusDiscoveryPendingItem,
): CrmOrganizationSiteMutation {
  return {
    siteName: pending.siteName,
    province: pending.province || undefined,
    city: pending.city || undefined,
    district: pending.district || undefined,
    address: pending.address,
    geoStatus: "unverified",
    isMainSite: false,
    isDisabled: false,
  };
}

/** 展示校区发现结果及待确认候选。 */
function CampusDiscoveryPanel({
  summary,
  retrying,
  adoptingKey,
  onRetry,
  onAdoptCandidate,
  onAdoptDraft,
}: {
  summary: CrmCampusDiscoverySummary;
  retrying: boolean;
  adoptingKey?: string;
  onRetry?: () => Promise<void>;
  onAdoptCandidate?: (
    pending: CrmCampusDiscoveryPendingItem,
    candidate: CrmCampusDiscoveryCandidate,
  ) => Promise<void>;
  onAdoptDraft?: (pending: CrmCampusDiscoveryPendingItem) => Promise<void>;
}) {
  const statusLabel = {
    confirmed: "已确认",
    needs_confirmation: "待确认",
    retryable_failure: "失败可重试",
    not_applicable: "不适用",
  }[summary.status];
  return (
    <Card className="crm-organization-card" aria-label="高校校区发现结果">
      <div className="crm-organization-campus-discovery">
        <div>
          <strong>校区发现：{statusLabel}</strong>
          <span>发现 {summary.discoveredCount} 个</span>
          <span>自动新增 {summary.createdCount} 个</span>
          <span>已存在 {summary.existingCount} 个</span>
          <span>待确认 {summary.pendingCount} 个</span>
        </div>
        {summary.pendingItems.length ? (
          <div className="crm-organization-campus-discovery-pending" aria-label="待确认校区">
            {summary.pendingItems.map((pending) => {
              const draftKey = `draft:${pending.siteName}`;
              return (
                <div
                  key={`${pending.siteName}:${pending.reason}`}
                  className="crm-organization-campus-discovery-pending-item"
                >
                  <div>
                    <strong>{pending.siteName}</strong>
                    <p>{pending.reason}</p>
                    {pending.address ? (
                      <small>
                        {formatCampusDiscoveryLocation(
                          pending.province,
                          pending.city,
                          pending.district,
                          pending.address,
                        )}
                      </small>
                    ) : (
                      <small>暂无可采纳地址，请人工新增校区并填写正确详细地址</small>
                    )}
                  </div>
                  {pending.candidates.length ? (
                    <ul aria-label={`${pending.siteName} 候选`}>
                      {pending.candidates.slice(0, 10).map((candidate) => {
                        const key = `${pending.siteName}:${candidate.poiId || candidate.name}`;
                        return (
                          <li key={key}>
                            <div>
                              <span>{candidate.name}</span>
                              <small>
                                {formatCampusDiscoveryLocation(
                                  candidate.province,
                                  candidate.city,
                                  candidate.district,
                                  candidate.address,
                                )}
                              </small>
                            </div>
                            {onAdoptCandidate ? (
                              <Button
                                size="mini"
                                type="primary"
                                loading={adoptingKey === key}
                                disabled={Boolean(adoptingKey) || retrying}
                                aria-label={`采用 ${pending.siteName} 候选 ${candidate.name}`}
                                onClick={() => onAdoptCandidate(pending, candidate)}
                              >
                                采用创建
                              </Button>
                            ) : null}
                          </li>
                        );
                      })}
                    </ul>
                  ) : onAdoptDraft && pending.address ? (
                    <Button
                      size="mini"
                      type="outline"
                      loading={adoptingKey === draftKey}
                      disabled={Boolean(adoptingKey) || retrying}
                      aria-label={`采用草稿创建 ${pending.siteName}`}
                      onClick={() => onAdoptDraft(pending)}
                    >
                      按草稿创建
                    </Button>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : summary.failureReasons.length ? (
          <ul className="crm-organization-campus-discovery-reasons">
            {summary.failureReasons.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
        ) : null}
        {onRetry ? (
          <Button loading={retrying} disabled={retrying || Boolean(adoptingKey)} onClick={onRetry}>
            重试校区发现
          </Button>
        ) : null}
      </div>
    </Card>
  );
}

/** 校区发现进行中的分阶段过程（治理校区 LLM 枚举 → 逐校区核验）。 */
const campusDiscoveryProgressSteps = [
  { atMs: 0, label: "正在按历史治理校区规则识别全部校区…" },
  { atMs: 8000, label: "正在逐校区核验地址与坐标…" },
] as const;

/** 展示校区发现过程中的阶段进度。 */
function CampusDiscoveryProgressPanel({ active }: { active: boolean }) {
  const [activeStepIndex, setActiveStepIndex] = useState(0);

  useEffect(() => {
    if (!active) {
      setActiveStepIndex(0);
      return;
    }
    setActiveStepIndex(0);
    const timers = campusDiscoveryProgressSteps.slice(1).map((step, index) =>
      window.setTimeout(() => setActiveStepIndex(index + 1), step.atMs),
    );
    return () => {
      for (const timer of timers) {
        window.clearTimeout(timer);
      }
    };
  }, [active]);

  if (!active) {
    return null;
  }

  return (
    <div
      className="crm-organization-campus-discovery-progress"
      aria-live="polite"
      aria-label="高校校区发现进度"
    >
      <strong>校区发现进行中</strong>
      <ol>
        {campusDiscoveryProgressSteps.map((step, index) => {
          const state =
            index < activeStepIndex ? "done" : index === activeStepIndex ? "active" : "pending";
          return (
            <li key={step.label} data-state={state}>
              {step.label}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/** 新建高校保存过程（仅主档；校区与组织单元均在保存后异步触发）。 */
const organizationCreateProgressSteps = [
  { atMs: 0, label: "正在保存学校主档…" },
] as const;

/** 展示新建高校保存与自动发现过程。 */
function OrganizationCreateProgressPanel({ active }: { active: boolean }) {
  const [activeStepIndex, setActiveStepIndex] = useState(0);

  useEffect(() => {
    if (!active) {
      setActiveStepIndex(0);
      return;
    }
    setActiveStepIndex(0);
    const timers = organizationCreateProgressSteps.slice(1).map((step, index) =>
      window.setTimeout(() => setActiveStepIndex(index + 1), step.atMs),
    );
    return () => {
      for (const timer of timers) {
        window.clearTimeout(timer);
      }
    };
  }, [active]);

  if (!active) {
    return null;
  }

  return (
    <div
      className="crm-organization-campus-discovery-progress"
      aria-live="polite"
      aria-label="学校保存进度"
    >
      <strong>保存进行中</strong>
      <ol>
        {organizationCreateProgressSteps.map((step, index) => {
          const state =
            index < activeStepIndex ? "done" : index === activeStepIndex ? "active" : "pending";
          return (
            <li key={step.label} data-state={state}>
              {step.label}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/** 学校新增与编辑右侧抽屉。 */
function OrganizationMutationModal({
  state,
  errorMessage,
  geoResolving,
  geoStatus,
  geoResult,
  onClose,
  onCreate,
  onUpdate,
  onResolveGeo,
}: {
  state: OrganizationModalState | null;
  errorMessage: string;
  geoResolving: boolean;
  geoStatus: GeoOperationStatus;
  geoResult?: GeoBusinessVerifyResult;
  onClose: () => void;
  onCreate: (payload: CrmOrganizationMutation) => Promise<void>;
  onUpdate: (item: CrmOrganizationItem, payload: CrmOrganizationMutation) => Promise<void>;
  onResolveGeo: (payload: CrmOrganizationMutation) => Promise<CrmOrganizationMutation>;
}) {
  const [payload, setPayload] = useState<CrmOrganizationMutation>(emptyCrmOrganizationMutation);
  const [saving, setSaving] = useState(false);
  const [localErrorMessage, setLocalErrorMessage] = useState("");
  const savingRef = useRef(false);
  const displayErrorMessage = localErrorMessage || errorMessage;

  useEffect(() => {
    if (!state) {
      setSaving(false);
      savingRef.current = false;
      setLocalErrorMessage("");
      return;
    }
    setLocalErrorMessage("");
    if (state.type === "edit") {
      setPayload({
        orgName: state.organization.orgName,
        orgType: state.organization.orgType,
        orgSubtype: state.organization.orgSubtype,
        province: state.organization.province,
        city: state.organization.city,
        district: state.organization.district,
        address: state.organization.address,
        isDisabled: state.organization.isDisabled,
        remark: state.organization.remark,
      });
      return;
    }
    setPayload(emptyCrmOrganizationMutation);
  }, [state]);

  /** 提交学校表单。 */
  async function handleOk() {
    if (!state || savingRef.current) {
      return;
    }
    if (!payload.orgName.trim()) {
      setLocalErrorMessage("请填写学校名称。");
      return;
    }
    if (!payload.province?.trim() || !payload.city?.trim()) {
      setLocalErrorMessage("请选择省份和城市。");
      return;
    }
    savingRef.current = true;
    setSaving(true);
    setLocalErrorMessage("");
    try {
      if (state.type === "create") {
        await onCreate(payload);
      } else {
        await onUpdate(state.organization, payload);
      }
    } catch {
      return;
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
    onClose();
  }

  /** 调用高德识别并回填学校组织省市区。 */
  async function handleResolveGeo() {
    setPayload(await onResolveGeo(payload));
  }

  /** 采用待修正候选回填学校组织省市区与地址。 */
  function handleSelectGeoCandidate(candidate: GeoBusinessCandidate) {
    setPayload(applyGeoCandidateToOrganizationMutation(payload, candidate));
  }

  const provinceOptions = withCurrentValueOption(getProvinceOptions(), payload.province);
  const cityOptions = withCurrentValueOption(
    payload.province ? getCityOptions(payload.province) : [],
    payload.city,
  );
  const districtOptions = withCurrentValueOption(
    payload.province && payload.city ? getDistrictOptions(payload.province, payload.city) : [],
    payload.district,
  );
  const useManualDistrictInput = shouldUseManualDistrictInput(payload.province, payload.city);
  const showCreateProgress =
    saving && state?.type === "create" && payload.orgType === crmOrganizationCollegeType;

  return (
    <LocalDrawer
      title={state?.type === "edit" ? "编辑学校组织" : "新增学校组织"}
      visible={Boolean(state)}
      onCancel={onClose}
      onOk={handleOk}
      okText="保存"
      cancelText="取消"
      confirmLoading={saving}
    >
      {displayErrorMessage ? <div className="crm-organization-error">{displayErrorMessage}</div> : null}
      <OrganizationCreateProgressPanel active={showCreateProgress} />
      <GeoOperationPanel
        status={geoStatus}
        result={geoResult}
        onRetry={() => void handleResolveGeo()}
        onSelectCandidate={handleSelectGeoCandidate}
      />
      <div className="crm-organization-form-grid">
        <Field label="学校名称" required>
          <Input
            aria-label="学校名称"
            value={payload.orgName}
            onChange={(orgName) => {
              setLocalErrorMessage("");
              setPayload({ ...payload, orgName });
            }}
          />
        </Field>
        <Field label="单位类型" required>
          <Select
            aria-label="单位类型"
            options={crmOrganizationTypeOptions.map((value) => ({ label: value, value }))}
            value={payload.orgType}
            onChange={(orgType) => setPayload({
              ...payload,
              orgType,
              orgSubtype:
                orgType === crmOrganizationCollegeType
                  ? (payload.orgSubtype ?? crmOrganizationSubtypeOptions[0])
                  : undefined,
            })}
          />
        </Field>
        {payload.orgType === crmOrganizationCollegeType ? (
          <Field label="高校子类型">
            <Select
              aria-label="高校子类型"
              options={crmOrganizationSubtypeOptions.map((value) => ({ label: value, value }))}
              value={payload.orgSubtype}
              onChange={(orgSubtype) => setPayload({ ...payload, orgSubtype })}
            />
          </Field>
        ) : null}
        <Field label="省份" required>
          <Select
            aria-label="省份"
            allowClear
            showSearch
            filterOption={filterRegionSelectOption}
            options={toSelectOptions(provinceOptions)}
            value={payload.province || undefined}
            onChange={(province) => {
              setLocalErrorMessage("");
              setPayload({ ...payload, province, city: "", district: "" });
            }}
          />
        </Field>
        <Field label="城市" required>
          <Select
            aria-label="城市"
            allowClear
            showSearch
            disabled={!payload.province}
            filterOption={filterRegionSelectOption}
            options={toSelectOptions(cityOptions)}
            value={payload.city || undefined}
            onChange={(city) => {
              setLocalErrorMessage("");
              setPayload({ ...payload, city, district: "" });
            }}
          />
        </Field>
        <Field label="区县">
          {useManualDistrictInput ? (
            <Input
              aria-label="区县"
              value={payload.district ?? ""}
              onChange={(district) => setPayload({ ...payload, district })}
            />
          ) : (
            <Select
              aria-label="区县"
              allowClear
              showSearch
              disabled={!payload.city}
              filterOption={filterRegionSelectOption}
              options={toSelectOptions(districtOptions)}
              value={payload.district || undefined}
              onChange={(district) => setPayload({ ...payload, district: district || "" })}
            />
          )}
        </Field>
        <Field label="详细地址" fullWidth>
          <Input
            aria-label="详细地址"
            value={payload.address ?? ""}
            onChange={(address) => setPayload({ ...payload, address })}
          />
        </Field>
        <Field label="状态">
          <Switch
            aria-label="学校状态"
            checked={!payload.isDisabled}
            checkedText="启用"
            uncheckedText="停用"
            onChange={(checked) => setPayload({ ...payload, isDisabled: !checked })}
          />
        </Field>
        <Field label="备注" fullWidth>
          <Input.TextArea
            aria-label="备注"
            value={payload.remark ?? ""}
            onChange={(remark) => setPayload({ ...payload, remark })}
          />
        </Field>
        <div className="crm-organization-form-grid__full">
          <Button
            icon={<MapPin size={16} />}
            loading={geoResolving}
            disabled={geoResolving || !payload.orgName.trim()}
            onClick={handleResolveGeo}
          >
            高德识别省市区
          </Button>
        </div>
      </div>
    </LocalDrawer>
  );
}

/** 学校校区维护右侧抽屉。 */
function SiteManagementModal({
  canManage,
  canDelete,
  organization,
  loading,
  errorMessage,
  geoResolving,
  geoStatus,
  geoResult,
  campusDiscoverySummary,
  campusDiscoveryRetrying,
  siteModalState,
  onClose,
  onOpenCreateSite,
  onOpenEditSite,
  onCloseSiteModal,
  onCreateSite,
  onUpdateSite,
  onDeleteSite,
  onResolveGeo,
  onPersistAdoptedSiteGeo,
  onAdoptDiscoveryPending,
  onDiscoverCampuses,
}: {
  canManage: boolean;
  canDelete: boolean;
  organization: CrmOrganizationItem | null;
  loading: boolean;
  errorMessage: string;
  geoResolving: boolean;
  geoStatus: GeoOperationStatus;
  geoResult?: GeoBusinessVerifyResult;
  campusDiscoverySummary?: CrmCampusDiscoverySummary;
  campusDiscoveryRetrying: boolean;
  siteModalState: SiteModalState | null;
  onClose: () => void;
  onOpenCreateSite: () => void;
  onOpenEditSite: (site: CrmOrganizationSiteItem) => void;
  onCloseSiteModal: () => void;
  onCreateSite: (payload: CrmOrganizationSiteMutation) => Promise<void>;
  onUpdateSite: (site: CrmOrganizationSiteItem, payload: CrmOrganizationSiteMutation) => Promise<void>;
  onDeleteSite: (site: CrmOrganizationSiteItem) => Promise<void>;
  onResolveGeo: (
    payload: CrmOrganizationSiteMutation,
    site?: CrmOrganizationSiteItem,
  ) => Promise<CrmOrganizationSiteMutation>;
  onPersistAdoptedSiteGeo?: (
    site: CrmOrganizationSiteItem,
    payload: CrmOrganizationSiteMutation,
  ) => Promise<CrmOrganizationSiteMutation>;
  onAdoptDiscoveryPending?: (payload: CrmOrganizationSiteMutation) => Promise<void>;
  onDiscoverCampuses?: () => Promise<void>;
}) {
  const [adoptingKey, setAdoptingKey] = useState<string>();
  const canDiscover =
    Boolean(onDiscoverCampuses) &&
    organization?.orgType === crmOrganizationCollegeType &&
    !organization.isDisabled;

  /** 采用待确认候选并保存为新校区。 */
  async function adoptPending(
    key: string,
    payload: CrmOrganizationSiteMutation,
  ) {
    if (!onAdoptDiscoveryPending || !canManage) {
      return;
    }
    setAdoptingKey(key);
    try {
      await onAdoptDiscoveryPending(payload);
    } finally {
      setAdoptingKey(undefined);
    }
  }

  return (
    <LocalDrawer
      title={organization ? `${organization.orgName} / 校区维护` : "校区维护"}
      visible={Boolean(organization)}
      onCancel={onClose}
      footer={null}
      width="48%"
    >
      {loading ? <div className="crm-organization-empty">正在加载校区...</div> : null}
      {errorMessage ? (
        <div className="crm-organization-error" role="alert">
          {errorMessage}
        </div>
      ) : null}
      {organization ? (
        <div className="crm-organization-sites">
          <div className="crm-organization-site-actions">
            <Button type="primary" icon={<Plus size={16} />} disabled={!canManage} onClick={onOpenCreateSite}>
              新增校区
            </Button>
            {canDiscover ? (
              <Button
                icon={<Search size={16} />}
                disabled={!canManage || campusDiscoveryRetrying}
                loading={campusDiscoveryRetrying}
                onClick={onDiscoverCampuses}
              >
                自动发现校区
              </Button>
            ) : null}
          </div>
          {campusDiscoveryRetrying ? <CampusDiscoveryProgressPanel active /> : null}
          {!campusDiscoveryRetrying && campusDiscoverySummary ? (
            <CampusDiscoveryPanel
              summary={filterCampusDiscoverySummary(
                campusDiscoverySummary,
                organization.sites,
              )}
              retrying={campusDiscoveryRetrying}
              adoptingKey={adoptingKey}
              onRetry={
                campusDiscoverySummary.retryable && onDiscoverCampuses
                  ? onDiscoverCampuses
                  : undefined
              }
              onAdoptCandidate={
                canManage && onAdoptDiscoveryPending
                  ? (pending, candidate) =>
                      adoptPending(
                        `${pending.siteName}:${candidate.poiId || candidate.name}`,
                        buildSiteMutationFromDiscoveryCandidate(pending, candidate),
                      )
                  : undefined
              }
              onAdoptDraft={
                canManage && onAdoptDiscoveryPending
                  ? (pending) =>
                      adoptPending(
                        `draft:${pending.siteName}`,
                        buildSiteMutationFromDiscoveryDraft(pending),
                      )
                  : undefined
              }
            />
          ) : null}
          {organization.sites.map((site) => (
            <div key={site.id} className="crm-organization-site-card">
              <div>
                <strong>{site.siteName}</strong>
                <p>{[site.province, site.city, site.district, site.address].filter(Boolean).join(" ")}</p>
                {site.isMainSite ? <Tag color="arcoblue">主校区</Tag> : null}
              </div>
              <div className="crm-organization-row-actions">
                 <Button
                  aria-label={`编辑校区 ${site.siteName}`}
                  type="text"
                   icon={<Pencil size={15} />}
                   disabled={!canManage}
                  onClick={() => onOpenEditSite(site)}
                />
                <Popconfirm
                  position="left"
                  getPopupContainer={(node) =>
                    node.closest(".crm-organization-drawer") ?? document.body
                  }
                  title={`确认删除“${site.siteName}”？删除后不可恢复。`}
                  okText="删除"
                  cancelText="取消"
                  okButtonProps={{ status: "danger" }}
                  disabled={!canDelete}
                  onOk={() => onDeleteSite(site)}
                >
                  <span className="crm-organization-delete-trigger">
                    <Button
                      aria-label={`删除校区 ${site.siteName}`}
                      type="text"
                      status="danger"
                      disabled={!canDelete}
                      icon={<Trash2 size={15} />}
                    />
                  </span>
                </Popconfirm>
              </div>
            </div>
          ))}
        </div>
      ) : null}
      <SiteMutationModal
        state={siteModalState}
        errorMessage={errorMessage}
        geoResolving={geoResolving}
        geoStatus={geoStatus}
        geoResult={geoResult}
        onClose={onCloseSiteModal}
        onCreate={onCreateSite}
        onUpdate={onUpdateSite}
        onResolveGeo={onResolveGeo}
        onPersistAdoptedSiteGeo={onPersistAdoptedSiteGeo}
      />
    </LocalDrawer>
  );
}

/** 校区新增与编辑右侧抽屉。 */
function SiteMutationModal({
  state,
  errorMessage,
  geoResolving,
  geoStatus,
  geoResult,
  onClose,
  onCreate,
  onUpdate,
  onResolveGeo,
  onPersistAdoptedSiteGeo,
}: {
  state: SiteModalState | null;
  errorMessage: string;
  geoResolving: boolean;
  geoStatus: GeoOperationStatus;
  geoResult?: GeoBusinessVerifyResult;
  onClose: () => void;
  onCreate: (payload: CrmOrganizationSiteMutation) => Promise<void>;
  onUpdate: (site: CrmOrganizationSiteItem, payload: CrmOrganizationSiteMutation) => Promise<void>;
  onResolveGeo: (
    payload: CrmOrganizationSiteMutation,
    site?: CrmOrganizationSiteItem,
  ) => Promise<CrmOrganizationSiteMutation>;
  onPersistAdoptedSiteGeo?: (
    site: CrmOrganizationSiteItem,
    payload: CrmOrganizationSiteMutation,
  ) => Promise<CrmOrganizationSiteMutation>;
}) {
  const [payload, setPayload] = useState<CrmOrganizationSiteMutation>(emptyCrmOrganizationSiteMutation);

  useEffect(() => {
    if (!state) {
      return;
    }
    if (state.type === "edit") {
      setPayload({
        siteName: state.site.siteName,
        siteType: state.site.siteType,
        province: state.site.province,
        city: state.site.city,
        district: state.site.district,
        address: state.site.address,
        lng: state.site.lng,
        lat: state.site.lat,
        geoStatus: state.site.geoStatus,
        isMainSite: state.site.isMainSite,
        isDisabled: state.site.isDisabled,
        remark: state.site.remark,
      });
      return;
    }
    setPayload(emptyCrmOrganizationSiteMutation);
  }, [state]);

  /** 提交校区表单。 */
  async function handleOk() {
    if (!state) {
      return;
    }
    try {
      if (state.type === "create") {
        await onCreate(payload);
      } else {
        await onUpdate(state.site, payload);
      }
    } catch {
      return;
    }
    onClose();
  }

  /** 使用 GEO 识别结果回填校区表单；编辑态成功后立即落库。 */
  async function handleResolveGeo() {
    const site = state?.type === "edit" ? state.site : undefined;
    setPayload(await onResolveGeo(payload, site));
  }

  /** 采用待修正候选回填校区表单；编辑态同步落库。 */
  async function handleSelectGeoCandidate(candidate: GeoBusinessCandidate) {
    const next = applyGeoCandidateToSiteMutation(payload, candidate);
    if (state?.type === "edit" && onPersistAdoptedSiteGeo) {
      setPayload(await onPersistAdoptedSiteGeo(state.site, next));
      return;
    }
    setPayload(next);
  }

  const provinceOptions = withCurrentValueOption(getProvinceOptions(), payload.province);
  const cityOptions = withCurrentValueOption(
    payload.province ? getCityOptions(payload.province) : [],
    payload.city,
  );
  const districtOptions = withCurrentValueOption(
    payload.province && payload.city ? getDistrictOptions(payload.province, payload.city) : [],
    payload.district,
  );
  const useManualDistrictInput = shouldUseManualDistrictInput(payload.province, payload.city);

  return (
    <LocalDrawer
      title={state?.type === "edit" ? "编辑校区" : "新增校区"}
      visible={Boolean(state)}
      onCancel={onClose}
      onOk={handleOk}
      okText="保存"
      cancelText="取消"
    >
      {errorMessage ? <div className="crm-organization-error">{errorMessage}</div> : null}
      <GeoOperationPanel
        key={state?.type === "edit" ? `site-geo-${state.site.id}` : "site-geo-create"}
        status={geoStatus}
        result={geoResult}
        onRetry={() => void handleResolveGeo()}
        onSelectCandidate={handleSelectGeoCandidate}
      />
      <div className="crm-organization-form-grid">
        <Field label="校区名称" required>
          <Input
            aria-label="校区名称"
            value={payload.siteName}
            onChange={(siteName) => setPayload({ ...payload, siteName })}
          />
        </Field>
        <Field label="校区类型">
          <Select
            aria-label="校区类型"
            options={crmOrganizationSiteTypeOptions.map((value) => ({ label: value, value }))}
            value={payload.siteType}
            onChange={(siteType) => setPayload({ ...payload, siteType })}
          />
        </Field>
        <Field label="省份" required>
          <Select
            aria-label="省份"
            allowClear
            showSearch
            filterOption={filterRegionSelectOption}
            options={toSelectOptions(provinceOptions)}
            value={payload.province || undefined}
            onChange={(province) => setPayload({ ...payload, province, city: "", district: "" })}
          />
        </Field>
        <Field label="城市" required>
          <Select
            aria-label="城市"
            allowClear
            showSearch
            disabled={!payload.province}
            filterOption={filterRegionSelectOption}
            options={toSelectOptions(cityOptions)}
            value={payload.city || undefined}
            onChange={(city) => setPayload({ ...payload, city, district: "" })}
          />
        </Field>
        <Field label="区县">
          {useManualDistrictInput ? (
            <Input
              aria-label="区县"
              value={payload.district ?? ""}
              onChange={(district) => setPayload({ ...payload, district })}
            />
          ) : (
            <Select
              aria-label="区县"
              allowClear
              showSearch
              disabled={!payload.city}
              filterOption={filterRegionSelectOption}
              options={toSelectOptions(districtOptions)}
              value={payload.district || undefined}
              onChange={(district) => setPayload({ ...payload, district: district || "" })}
            />
          )}
        </Field>
        <Field label="详细地址" fullWidth>
          <Input
            aria-label="详细地址"
            value={payload.address}
            onChange={(address) => setPayload({ ...payload, address })}
          />
        </Field>
        <Field label="经度">
          <Input
            aria-label="经度"
            value={String(payload.lng ?? "")}
            onChange={(lng) => setPayload({ ...payload, lng: toNumber(lng) })}
          />
        </Field>
        <Field label="纬度">
          <Input
            aria-label="纬度"
            value={String(payload.lat ?? "")}
            onChange={(lat) => setPayload({ ...payload, lat: toNumber(lat) })}
          />
        </Field>
        <Field label="主校区">
          <Switch
            aria-label="主校区"
            checked={payload.isMainSite}
            onChange={(isMainSite) => setPayload({ ...payload, isMainSite })}
          />
        </Field>
        <Field label="状态">
          <Switch
            aria-label="校区状态"
            checked={!payload.isDisabled}
            checkedText="启用"
            uncheckedText="停用"
            onChange={(checked) => setPayload({ ...payload, isDisabled: !checked })}
          />
        </Field>
        <div className="crm-organization-form-grid__full">
          <Button
            icon={<Building2 size={16} />}
            loading={geoResolving}
            disabled={geoResolving}
            onClick={handleResolveGeo}
          >
            {geoResolving ? "识别中..." : "GEO 识别并回填"}
          </Button>
        </div>
      </div>
    </LocalDrawer>
  );
}

/** 本功能域右侧抽屉，对齐考察点等后台表单的右抽屉交互。 */
function LocalDrawer({
  title,
  visible,
  onCancel,
  onOk,
  okText = "确定",
  cancelText = "取消",
  footer,
  children,
  width = "40%",
  confirmLoading = false,
}: {
  title: ReactNode;
  visible: boolean;
  onCancel: () => void;
  onOk?: () => Promise<void> | void;
  okText?: string;
  cancelText?: string;
  footer?: ReactNode | null;
  children: ReactNode;
  width?: number | string;
  confirmLoading?: boolean;
}) {
  return (
    <Drawer
      {...({
        role: "dialog",
        "aria-label": String(title),
        "aria-modal": "true",
      } as Record<string, string>)}
      className="crm-organization-drawer"
      title={title}
      visible={visible}
      placement="right"
      width={width}
      autoFocus
      focusLock
      unmountOnExit
      escToExit={!confirmLoading}
      maskClosable={!confirmLoading}
      closeIcon={
        <Button
          type="text"
          size="mini"
          aria-label="关闭"
          icon={<X size={16} />}
          disabled={confirmLoading}
        />
      }
      footer={
        footer === null
          ? null
          : (footer ?? (
              <>
                <Button onClick={onCancel} disabled={confirmLoading}>
                  {cancelText}
                </Button>
                <Button type="primary" loading={confirmLoading} onClick={onOk}>
                  {okText}
                </Button>
              </>
            ))
      }
      onCancel={confirmLoading ? () => undefined : onCancel}
    >
      <div className="crm-organization-drawer-body">{children}</div>
    </Drawer>
  );
}

/** 表单字段容器（上标签下控件，两列栅格中占一格）。 */
function Field({
  label,
  required = false,
  fullWidth = false,
  children,
}: {
  label: string;
  required?: boolean;
  fullWidth?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={`crm-organization-field${fullWidth ? " crm-organization-form-grid__full" : ""}`}>
      <span>
        {required ? <em aria-hidden="true">*</em> : null}
        {label}
      </span>
      {children}
    </div>
  );
}

/** 将输入框文本转换为可选数字。 */
function toNumber(value: string) {
  const normalized = value.trim();
  if (!normalized) {
    return undefined;
  }
  const numberValue = Number(normalized);
  return Number.isFinite(numberValue) ? numberValue : undefined;
}

/** 将行政区名称转换为 Arco Select 选项。 */
function toSelectOptions(options: string[]) {
  return options.map((option) => ({ label: option, value: option }));
}

/** 编辑历史脏数据时保留当前值，避免 Select 丢失显示。 */
function withCurrentValueOption(options: string[], currentValue?: string) {
  return currentValue && !options.includes(currentValue) ? [currentValue, ...options] : options;
}

/** 将筛选下拉挂到 body，避免弹层被表格区域挡住。 */
function filterPopupContainer() {
  return document.body;
}

/** 按输入文本过滤行政区下拉选项。 */
function filterRegionSelectOption(inputValue: string, option: ReactElement) {
  const value = (option.props as { value?: unknown } | undefined)?.value;
  return String(value ?? "").toLowerCase().includes(inputValue.toLowerCase());
}
